/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/userLists.config.json";
import axios from "axios";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_SUPPORTED_ROLES } from "../graphql/getSupportedRoles.query";
import { VApplication } from "../../../application";
import moment from "moment-timezone";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ISupportedRoleMethods extends IPluginMethodMap {
  getSupportedRolesProps(args: any): Promise<any>;
}

export class SupportedRoleService implements IAgentPlugin {
  readonly methods: ISupportedRoleMethods;

  constructor() {
    this.methods = {
      getSupportedRolesProps: this.getSupportedRolesProps.bind(this),
    };
  }

  async getSupportedRolesProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.query({
          query: GET_SUPPORTED_ROLES,
          fetchPolicy: "network-only",
        });
        if (res?.data) {
          return {
            availableRole: this._getRole(res?.data?.getSupportedRoles),
          };
        }
      }
    } catch (err: any) {
      console.log(`error in table data`, err);
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }
      throw err;
    }
  }

  private _getRole(roles: any[]) {
    const allowedRoles = [
      "masq",
      "admin",
      "committeeMember",
      "cashier",
      "superAdmin",
      "cashSupervisor",
      "supervisor",
      "production",
    ];
    const roleLabels: { [key: string]: string } = {
      masq: "Masquerader",
      admin: "Admin",
      committeeMember: "Committee Member",
      cashier: "Cashier",
      superAdmin: "Super Admin",
      cashSupervisor: "Cash Supervisor",
      supervisor: "Supervisor",
      production: "Production",
    };

    // Use optional chaining (?.) to handle cases where `roles` might be undefined or null
    const rolesList = roles?.content
      .filter((role) => {
        return allowedRoles.includes(role.name);
      })
      .map((role) => {
        const label = roleLabels[role.name] || role.name; // Use the predefined label or the original name if not found in roleLabels
        return {
          label,
          id: role.uuid,
        };
      });
    return rolesList;
  }
}
